import MapPicker from "@/components/Register/mapPicker.component";
import { ValidationErrors } from "@/components/UserManagementComponents/Dialogs/DialogViewUser/viewUserDialog.component";
import {
  INDUSTRY_TYPES,
  RESERVED_USER_CREATE_ID,
} from "@/constants/util.constants";
import { Alert, AlertDescription } from "@/shadcn/components/ui/alert";
import { Badge } from "@/shadcn/components/ui/badge";
import { Button } from "@/shadcn/components/ui/button";
import { Card, CardContent } from "@/shadcn/components/ui/card";
import { Input } from "@/shadcn/components/ui/input";
import { Label } from "@/shadcn/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcn/components/ui/tabs";
import { UserAdminUpdateProfileDetails } from "@/types";
import { Building, MapPin, Plus, Trash } from "lucide-react";
import React, { useEffect, useState } from "react";

type CompanyDetailsProps = {
  userDetails: UserAdminUpdateProfileDetails;
  setUserDetails: React.Dispatch<
    React.SetStateAction<UserAdminUpdateProfileDetails | null>
  >;
  errors: ValidationErrors;
};

// Local editable types to decouple DB structure from UI state

const CompanyDetails: React.FC<CompanyDetailsProps> = ({
  userDetails,
  setUserDetails,
  errors,
}) => {
  // Directly use userDetails.companies for state, to keep UI in sync with parent
  // This avoids unnecessary local state and ensures updates propagate upward
  const companies = userDetails.companies;
  const [activeCompanyId, setActiveCompanyId] = useState<string>(
    companies[0]?.company_id || ""
  );
  const [activeBranchId, setActiveBranchId] = useState<string>(
    companies[0]?.branches[0]?.branch_id || ""
  );

  // Update a company in userDetails, using setUserDetails to ensure parent state is updated
  const updateCompany = (
    companyId: string,
    updates: Partial<UserAdminUpdateProfileDetails["companies"][0]>
  ) => {
    setUserDetails((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        companies: prev.companies.map((c) =>
          c.company_id === companyId ? { ...c, ...updates } : c
        ),
      };
    });
  };

  // Update a branch in userDetails, using setUserDetails for top-level state
  const updateBranch = (
    companyId: string,
    branchId: string,
    updates: Partial<
      UserAdminUpdateProfileDetails["companies"][0]["branches"][0]
    >
  ) => {
    setUserDetails((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        companies: prev.companies.map((c) =>
          c.company_id === companyId
            ? {
                ...c,
                branches: c.branches.map((b) =>
                  b.branch_id === branchId ? { ...b, ...updates } : b
                ),
              }
            : c
        ),
      };
    });
  };

  // Keep active branch in sync with active company, but only reset if the current activeBranchId is not present
  useEffect(() => {
    const foundCompany = companies.find(
      (c) => c.company_id === activeCompanyId
    );
    if (!foundCompany) return;
    const branchExists = foundCompany.branches.some(
      (b) => b.branch_id === activeBranchId
    );
    // Only update if the current activeBranchId is not present in the active company
    if (!branchExists) {
      setActiveBranchId(
        foundCompany.branches.length > 0
          ? foundCompany.branches[0].branch_id
          : ""
      );
    }
  }, [activeCompanyId, companies, activeBranchId]);

  // Add a new company to userDetails, using setUserDetails to keep state in sync with parent
  const addCompany = () => {
    const now = Date.now();
    const newCompanyId = `${RESERVED_USER_CREATE_ID}_company-${now}`;
    const newBranchId = `${RESERVED_USER_CREATE_ID}_branch-${now}`;
    setUserDetails((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        companies: [
          ...prev.companies,
          {
            company_id: newCompanyId,
            account_number: "",
            company_type: "business",
            company_name: "",
            registration_number: "",
            tax_number: "",
            industry_type: "Miscellaneous / Other",
            phone_number: "",
            // company_created_at: new Date(),
            // company_updated_at: new Date(),
            branches: [
              {
                branch_id: newBranchId,
                branch_name: "Main Branch",
                branch_address1: "",
                branch_address2: "",
                branch_suburb: "",
                branch_city: "",
                branch_province: "",
                branch_country: "South Africa",
                branch_office_park_name: "",
                branch_pin_code: "",
                branch_latitude: "0",
                branch_longitude: "0",
                // branch_created_at: new Date(),
              },
            ],
          },
        ],
      };
    });
    setActiveCompanyId(newCompanyId);
    setActiveBranchId(newBranchId);
  };

  // Add a new branch to a company in userDetails, using setUserDetails for top-level state
  const addBranch = (companyId: string) => {
    const now = Date.now();
    const newBranchId = `${RESERVED_USER_CREATE_ID}_branch-${now}`;
    setUserDetails((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        companies: prev.companies.map((c) =>
          c.company_id === companyId
            ? {
                ...c,
                branches: [
                  ...c.branches,
                  {
                    branch_id: newBranchId,
                    branch_name: "New Branch",
                    branch_address1: "",
                    branch_address2: "",
                    branch_suburb: "",
                    branch_city: "",
                    branch_province: "",
                    branch_country: "South Africa",
                    branch_office_park_name: "",
                    branch_pin_code: "",
                    branch_latitude: "0",
                    branch_longitude: "0",
                    // branch_created_at: new Date(),
                  },
                ],
              }
            : c
        ),
      };
    });
    setActiveBranchId(newBranchId);
  };

  // Remove a branch from a company, only for newly created branches (id includes RESERVED_USER_CREATE_ID)
  const deleteBranch = (companyId: string, branchId: string) => {
    setUserDetails((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        companies: prev.companies.map((c) =>
          c.company_id === companyId
            ? {
                ...c,
                branches: c.branches.filter((b) => b.branch_id !== branchId),
              }
            : c
        ),
      };
    });
    // If the deleted branch was active, set active to first branch (if any)
    if (activeBranchId === branchId) {
      const foundCompany = companies.find((c) => c.company_id === companyId);
      const remaining =
        foundCompany?.branches.filter((b) => b.branch_id !== branchId) || [];
      setActiveBranchId(remaining[0]?.branch_id || "");
    }
  };

  // Remove a company, only for newly created companies (id includes RESERVED_USER_CREATE_ID)
  const deleteCompany = (companyId: string) => {
    setUserDetails((prev) => {
      if (!prev) return prev;
      const updatedCompanies = prev.companies.filter(
        (c) => c.company_id !== companyId
      );
      // Set active company/branch to first available if needed
      let newActiveCompanyId = activeCompanyId;
      let newActiveBranchId = activeBranchId;
      if (activeCompanyId === companyId && updatedCompanies.length > 0) {
        newActiveCompanyId = updatedCompanies[0].company_id;
        newActiveBranchId = updatedCompanies[0].branches[0]?.branch_id || "";
      } else if (updatedCompanies.length === 0) {
        newActiveCompanyId = "";
        newActiveBranchId = "";
      }
      setActiveCompanyId(newActiveCompanyId);
      setActiveBranchId(newActiveBranchId);
      return {
        ...prev,
        companies: updatedCompanies,
      };
    });
  };

  // Find the active company and branch using the DB structure, to keep UI in sync with parent
  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);
  const activeBranch = activeCompany?.branches.find(
    (b) => b.branch_id === activeBranchId
  );

  function changeCompanyType(companyId: string, type: "business" | "private") {
    if (type === "private") {
      // If changing to private, clear all branches
      updateCompany(companyId, {
        company_type: type,
        company_name: "Private",
        industry_type: "Individual",
        registration_number: undefined,
        tax_number: undefined,
        phone_number: "",
      });
    } else {
      // If changing to business, set default values
      updateCompany(companyId, {
        company_type: type,
        company_name: "",
        industry_type: "Miscellaneous / Other",
        registration_number: "",
        tax_number: "",
        phone_number: "",
      });
    }
  }

  return (
    <div className="mt-8">
      <div className="mb-4 text-xl font-semibold">
        Company & Branches Details
      </div>
      {errors.companies && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="text-sm !text-red-500 opacity-95">
            {errors.companies.map((error, index) => (
              <div key={index} className="text-sm text-red-500">
                {error}
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}
      {companies.length > 0 && (
        <Tabs value={activeCompanyId} onValueChange={setActiveCompanyId}>
          <TabsList className="mb-4 flex h-auto min-h-fit w-full flex-wrap justify-start gap-2">
            {companies.map((company) => (
              <TabsTrigger
                key={company.company_id}
                value={company.company_id}
                className="flex h-auto min-w-fit flex-shrink-0 cursor-pointer items-center gap-2"
              >
                <div className="flex items-center gap-2 duration-200 hover:text-white">
                  <Building className="h-4 w-4" />
                  <span className="max-w-[120px] truncate">
                    {company.company_name || "(No Name)"}
                  </span>
                  {/* Show delete button only for new companies (id includes RESERVED_USER_CREATE_ID) */}
                  {company.company_id.includes(RESERVED_USER_CREATE_ID) &&
                    companies.length > 1 && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCompany(company.company_id);
                        }}
                        className="duration-100 hover:text-white hover:drop-shadow-[0px_0px_1px_#ffffffbf]"
                        title="Delete company"
                      >
                        <Trash className="h-4 w-4" />
                      </div>
                    )}
                </div>
              </TabsTrigger>
            ))}
          </TabsList>
          {companies.map((company) => (
            <TabsContent
              key={company.company_id}
              value={company.company_id}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor={`company-type-${company.company_id}`}>
                  Company Type
                </Label>
                <Select
                  value={company.company_type}
                  onValueChange={(value) =>
                    changeCompanyType(
                      company.company_id,
                      value as "business" | "private"
                    )
                  }
                >
                  <SelectTrigger className="w-full max-w-full truncate">
                    <SelectValue placeholder="Select company type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="business">Business</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-4 space-y-4 md:grid-cols-2">
                {company.company_type !== "private" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor={`company-name-${company.company_id}`}>
                        Company Name
                      </Label>
                      <Input
                        id={`company-name-${company.company_id}`}
                        value={company.company_name}
                        onChange={(e) =>
                          updateCompany(company.company_id, {
                            company_name: e.target.value,
                          })
                        }
                        placeholder="Enter company name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor={`company-accountno-${company.company_id}`}
                      >
                        Company Account Number
                      </Label>
                      <Input
                        id={`company-accountno-${company.company_id}`}
                        value={company.account_number || ""}
                        onChange={(e) =>
                          updateCompany(company.company_id, {
                            account_number: e.target.value,
                          })
                        }
                        placeholder="Enter company account number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`company-regno-${company.company_id}`}>
                        Registration Number
                      </Label>
                      <Input
                        id={`company-regno-${company.company_id}`}
                        value={company.registration_number || ""}
                        onChange={(e) =>
                          updateCompany(company.company_id, {
                            registration_number: e.target.value,
                          })
                        }
                        placeholder="Enter registration number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`company-vatno-${company.company_id}`}>
                        VAT Number
                      </Label>
                      <Input
                        id={`company-vatno-${company.company_id}`}
                        value={company.tax_number || ""}
                        onChange={(e) =>
                          updateCompany(company.company_id, {
                            tax_number: e.target.value,
                          })
                        }
                        placeholder="Enter VAT number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`company-industry-${company.company_id}`}>
                        Industry Type
                      </Label>
                      <Select
                        value={company.industry_type}
                        onValueChange={(value) =>
                          updateCompany(company.company_id, {
                            industry_type: value,
                          })
                        }
                      >
                        <SelectTrigger className="w-full max-w-full truncate">
                          <SelectValue placeholder="Select industry type" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRY_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`company-contact-${company.company_id}`}>
                        Company Contact Number
                      </Label>
                      <Input
                        id={`company-contact-${company.company_id}`}
                        value={company.phone_number}
                        onChange={(e) =>
                          updateCompany(company.company_id, {
                            phone_number: e.target.value,
                          })
                        }
                        placeholder="Enter company contact number"
                      />
                    </div>
                  </>
                )}
              </div>
              {/* Branches */}
              <div className="space-y-4">
                <h4 className="mr-2 font-medium">Branches</h4>
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {company.branches.map((branch) => (
                      <div
                        key={branch.branch_id}
                        className="flex items-center gap-1"
                      >
                        <Badge
                          variant={
                            branch.branch_id === activeBranchId
                              ? "default"
                              : "secondary"
                          }
                          onClick={() => setActiveBranchId(branch.branch_id)}
                          className="cursor-pointer"
                        >
                          <MapPin className="mr-1 h-4 w-4" />
                          <span className="max-w-[100px] truncate">
                            {branch.branch_name || "(No Name)"}
                          </span>
                          {branch.branch_id.includes(RESERVED_USER_CREATE_ID) &&
                            company.branches.length > 1 && (
                              <div
                                onClick={() =>
                                  deleteBranch(
                                    company.company_id,
                                    branch.branch_id
                                  )
                                }
                                className={`duration-100 ${branch.branch_id === activeBranchId ? "hover:drop-shadow-[0px_0px_1px_#000000bf]" : "hover:text-white hover:drop-shadow-[0px_0px_1px_#ffffffbf]"}`}
                                title="Delete branch"
                              >
                                <Trash className="h-3 w-3" />
                              </div>
                            )}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Active Branch Form */}
                {activeBranch && (
                  <Card className="mt-2">
                    <CardContent className="space-y-2 pt-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor={`branch-name-${activeBranch.branch_id}`}
                        >
                          Branch Name
                        </Label>
                        <Input
                          id={`branch-name-${activeBranch.branch_id}`}
                          value={activeBranch.branch_name}
                          onChange={(e) =>
                            updateBranch(
                              company.company_id,
                              activeBranch.branch_id,
                              {
                                branch_name: e.target.value,
                              }
                            )
                          }
                          placeholder="Enter branch name"
                        />
                      </div>
                      <MapPicker
                        activeBranchID={activeBranch.branch_id}
                        address={{
                          addressLine1: activeBranch.branch_address1,
                          addressLine2: activeBranch.branch_address2 || "",
                          officeParkName:
                            activeBranch.branch_office_park_name || "",
                          suburb: activeBranch.branch_suburb || "",
                          city: activeBranch.branch_city,
                          province: activeBranch.branch_province || "",
                          country: activeBranch.branch_country,
                          postalCode: activeBranch.branch_pin_code,
                          latitude: Number(activeBranch.branch_latitude) || 0,
                          longitude: Number(activeBranch.branch_longitude) || 0,
                        }}
                        onAddressChange={(updated) =>
                          updateBranch(
                            company.company_id,
                            activeBranch.branch_id,
                            {
                              branch_address1: updated.addressLine1,
                              branch_address2: updated.addressLine2 || "",
                              branch_office_park_name:
                                updated.officeParkName || "",
                              branch_suburb: updated.suburb || "",
                              branch_city: updated.city,
                              branch_province: updated.province,
                              branch_country: updated.country,
                              branch_pin_code: updated.postalCode,
                              branch_latitude: String(updated.latitude),
                              branch_longitude: String(updated.longitude),
                            }
                          )
                        }
                      />
                    </CardContent>
                  </Card>
                )}
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={() => addBranch(company.company_id)}
                  variant="outline"
                  size="sm"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Additional Branch
                </Button>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}
      <div className="mt-4 flex justify-end">
        <Button
          type="button"
          onClick={addCompany}
          variant="outline"
          size="sm"
          disabled={companies.length === 0 ? false : false}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Additional Company
        </Button>
      </div>
    </div>
  );
};

export default CompanyDetails;
